import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon
} from "@heroicons/react/20/solid";
import { usePrivy } from "@privy-io/react-auth";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { A } from "@/atoms-react/a/A.react";
import { Button } from "@/atoms-react/button/Button.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Base } from "@/atoms-react/text/Text.react";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";
import type { KycoinAccount } from "@/features/kycoin/types";
import type { KypayAccount, WalletData } from "@/features/kypay/types";
import { Routes } from "@/features/router";
import { type identityPartners, type UserType } from "@/features/user/types";
import { Themes } from "@/types/theme";
import { TransfertCardHeader } from "./transfer-card-header";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { useUserCheckAccess } from "@/features/user/hooks/use-user-check-access";
import { companyDetails, MandateService } from "@/features/company/types";
import { preventEnterEvent } from "@/utils/keyEvents";
import { useDispatch } from "react-redux";
import {
  setAddWallet,
  setAuthenticationRequireHelper,
  setBankToCrypto,
  setCryptoToBank,
  setFeedBank,
  setWithdrawBank
} from "@/redux/slices/transfertActionSlice";
import { useAppSelector } from "@/redux/hooks";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";

type TransferCardType = {
  isAutoFetchingWallet: boolean;
  balanceKypay?: number;
  balanceKypayOnChain?: string;
  allowedToKypay?: boolean;
  isLoadingBalanceKypay?: boolean;
  isLoadingBalanceKypayOnChain?: boolean;
  wallet?: WalletData;
  isTokenyWalletFailed: boolean;
  hasOneTOTP?: boolean;
  isWalletLoading: boolean;
  currentAccount: KycoinAccount;
  refetchAccount: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<KypayAccount[], Error>>;

  identityPartner: identityPartners | null;
  balanceKypayOnChainCurrency?: string;
  allowedToPaticipate?: boolean;
  btnText?: string;
  companyDetails: companyDetails | null | undefined;
  userProfile: UserType | undefined;
  isWalletDataLoading: boolean;
};

export const TransfertCard = ({
  isAutoFetchingWallet,
  balanceKypay,
  btnText,
  allowedToPaticipate,
  balanceKypayOnChain,
  identityPartner,
  refetchAccount,
  hasOneTOTP,
  isLoadingBalanceKypay,
  isLoadingBalanceKypayOnChain,
  currentAccount,
  isWalletLoading,
  wallet,
  isTokenyWalletFailed,
  companyDetails,
  userProfile
}: TransferCardType) => {
  const { t } = useTranslation(["pass", "wallet", "atoms"]);
  const origin = useOrigin();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { checkPermission } = useUserCheckAccess();
  const actions = useAppSelector((state) => state.transfertActions);
  const { authenticated, ready, logout } = usePrivy();
  const { leUuid } = useCompanyContext();
  const walletExist =
    (wallet?.walletConnect && !authenticated) ||
    (wallet && wallet?.status !== "Qualified" && wallet?.status !== "TokenHolder");

  // Components //
  const FetchingWalletComponent = () =>
    isAutoFetchingWallet ? (
      <Base className="mb-2 items-end truncate text-primary text-sm md:text-base">
        {t("wallet:kypay.balance_card.wallet_validation")}
        <Loader className={twMerge("ml-2")} />
      </Base>
    ) : (
      <></>
    );

  return (
    <>
      <TransfertCardHeader identityPartner={identityPartner as identityPartners} origin={origin} />
      <div className="flex w-full flex-col rounded-sm border-2 border-slate-50 px-2 py-2 md:py-5 lg:flex-row lg:justify-between lg:py-3 dark:border-slate-700">
        <div className="flex flex-col justify-start lg:w-1/4">
          <div className="flex px-2">
            <div
              className={twMerge(
                "flex h-8 w-8 flex-row justify-center rounded-full bg-black lg:h-10 lg:w-10 dark:bg-transparent",
                !identityPartner?.logo && "bg-transparent"
              )}
            >
              <div className="flex flex-col justify-center">
                <img
                  alt={"logo"}
                  src={identityPartner?.logo ?? "/images/olkypass/kypayIcon.png"}
                  className="w-7"
                />
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <Base className="ml-1 font-semibold text-lg lg:ml-4 lg:text-xl">
                {" "}
                {identityPartner?.name ?? "KYPAY"}{" "}
              </Base>
            </div>
          </div>
        </div>
        <div className="flex w-full flex-col justify-center lg:w-1/2 lg:flex-row">
          <div className="flex flex-col p-4 lg:p-0">
            {!isLoadingBalanceKypay ? (
              <div className="flex flex-row justify-between lg:justify-start">
                {" "}
                <Base className="font-bold text-lg">EUR: </Base>{" "}
                <Base className="ml-2 font-bold text-lg lg:ml-6 lg:text-2xl">{balanceKypay} </Base>
              </div>
            ) : (
              <Loader />
            )}
            <div className="flex flex-row justify-between">
              <div className={twMerge("flex flex-col gap-2 lg:mt-0 lg:flex-row")}>
                {origin !== FromURL.None ? (
                  <Button
                    size="md"
                    theme={Themes.white}
                    disabled={
                      !checkPermission(null, userProfile, companyDetails, identityPartner) ||
                      !allowedToPaticipate
                    }
                    {...(!checkPermission(null, userProfile, companyDetails, identityPartner) && {
                      "data-tooltip": t("atoms:molecules.kyc_alert.kyc_warning")
                    })}
                    onClick={async () => {
                      await refetchAccount();
                      dispatch(setFeedBank(true));
                    }}
                    className={"mt-1 w-60 sm:w-max"}
                  >
                    {btnText}
                  </Button>
                ) : (
                  <Button
                    size="md"
                    theme={Themes.white}
                    disabled={
                      !checkPermission(null, userProfile, companyDetails, null) || !currentAccount
                    }
                    {...(!checkPermission(null, userProfile, companyDetails, null) && {
                      "data-tooltip": t("atoms:molecules.kyc_alert.kyc_warning")
                    })}
                    onClick={() => dispatch(setFeedBank(true))}
                    className={"mt-1 w-max"}
                  >
                    {t("wallet:kypay.button.feed")}
                  </Button>
                )}

                <>
                  <Button
                    size="md"
                    theme={Themes.white}
                    disabled={
                      origin === FromURL.Partner ||
                      !checkPermission(
                        MandateService.KYPAY_WITHDRAW,
                        userProfile,
                        companyDetails,
                        identityPartner
                      )
                    }
                    {...(!checkPermission(
                      MandateService.KYPAY_WITHDRAW,
                      userProfile,
                      companyDetails,
                      identityPartner
                    ) && {
                      "data-tooltip": t("atoms:molecules.kyc_alert.kyc_warning")
                    })}
                    onClick={() => {
                      if (hasOneTOTP) {
                        dispatch(setWithdrawBank(true));
                      } else {
                        dispatch(setAuthenticationRequireHelper(true));
                      }
                    }}
                    className={"mt-1 w-max"}
                  >
                    {t("wallet:kypay.button.withdraw")}
                  </Button>
                </>
              </div>
              <div className="flex flex-col justify-center">
                {((origin === FromURL.Partner &&
                  identityPartner?.mode === "TOKEN" &&
                  identityPartner?.token?.address) ||
                  origin === FromURL.None) && (
                  <Button
                    theme={Themes.iconButton}
                    icon={<ChevronDownIcon />}
                    size="lg"
                    className={twMerge(
                      "mb-2 h-10 w-10 rounded-full border-2 lg:hidden xl:mb-4",
                      (origin === FromURL.Partner ||
                        !checkPermission(null, userProfile, companyDetails, identityPartner) ||
                        identityPartner?.trigger_type === "PARTNER" ||
                        isWalletLoading ||
                        `${balanceKypay}` === "0,00" ||
                        walletExist ||
                        isTokenyWalletFailed) &&
                        "opacity-20"
                    )}
                    disabled={
                      origin === FromURL.Partner ||
                      !checkPermission(null, userProfile, companyDetails, identityPartner) ||
                      identityPartner?.trigger_type === "PARTNER" ||
                      isWalletLoading ||
                      `${balanceKypay}` === "0,00" ||
                      walletExist ||
                      isTokenyWalletFailed
                    }
                    {...(!checkPermission(null, userProfile, companyDetails, identityPartner)
                      ? {
                          "data-tooltip": t("atoms:molecules.kyc_alert.kyc_warning")
                        }
                      : { "data-tooltip": t("wallet:kypay.modal_deposit_funds.label.title") })}
                    onClick={async () => {
                      if (hasOneTOTP) {
                        if (
                          wallet &&
                          !isTokenyWalletFailed &&
                          wallet.walletConnect &&
                          !wallet.olkyWallet &&
                          !authenticated
                        )
                          return;
                        if (
                          !wallet?.walletAddress &&
                          !wallet?.walletConnect &&
                          ready &&
                          authenticated
                        ) {
                          await logout();
                        } else if (
                          !isTokenyWalletFailed &&
                          !wallet?.walletAddress &&
                          !wallet?.walletConnect
                        )
                          dispatch(setAddWallet(true));
                        else dispatch(setBankToCrypto(true));
                      } else {
                        dispatch(setAuthenticationRequireHelper(true));
                      }
                    }}
                  />
                )}
              </div>
            </div>

            <div className="mt-4 flex flex-row justify-start">
              <A
                onClick={() =>
                  navigate(
                    leUuid
                      ? `${Routes.account_transactions}/${leUuid}`
                      : Routes.account_transactions
                  )
                }
                className="text-black underline dark:text-white"
              >
                Transactions
              </A>
            </div>
          </div>
          {origin === FromURL.None && (
            <div className="flex w-full flex-row justify-center px-6 py-2 sm:hidden">
              <hr className="w-full border-2 border-slate-50 dark:border-slate-700" />
            </div>
          )}
          <div className="ml-8 hidden w-1/2 content-center justify-center lg:ml-0 lg:flex lg:w-full lg:flex-col">
            <div className="flex flex-row justify-center">
              {((origin === FromURL.Partner &&
                identityPartner?.mode === "TOKEN" &&
                identityPartner?.token?.address) ||
                origin === FromURL.None) && (
                <Button
                  theme={Themes.iconButton}
                  icon={<ChevronRightIcon />}
                  size="lg"
                  className={twMerge(
                    "mb-2 h-10 w-10 rounded-full rounded-full border-2 xl:mb-4",
                    (origin === FromURL.Partner ||
                      !checkPermission(null, userProfile, companyDetails, identityPartner) ||
                      identityPartner?.trigger_type === "PARTNER" ||
                      `${balanceKypay}` === "0,00" ||
                      isWalletLoading ||
                      walletExist ||
                      isTokenyWalletFailed) &&
                      "opacity-20"
                  )}
                  {...(!checkPermission(null, userProfile, companyDetails, identityPartner)
                    ? {
                        "data-tooltip": t("atoms:molecules.kyc_alert.kyc_warning")
                      }
                    : { "data-tooltip": t("wallet:kypay.modal_deposit_funds.label.title") })}
                  disabled={
                    origin === FromURL.Partner ||
                    !checkPermission(null, userProfile, companyDetails, identityPartner) ||
                    identityPartner?.trigger_type === "PARTNER" ||
                    `${balanceKypay}` === "0,00" ||
                    isWalletLoading ||
                    walletExist ||
                    isTokenyWalletFailed
                  }
                  onClick={async () => {
                    if (hasOneTOTP) {
                      if (
                        wallet &&
                        !isTokenyWalletFailed &&
                        wallet.walletConnect &&
                        !wallet.olkyWallet &&
                        !authenticated
                      )
                        return;
                      if (
                        !wallet?.walletAddress &&
                        !wallet?.walletConnect &&
                        ready &&
                        authenticated
                      ) {
                        await logout();
                        return;
                      }
                      if (!isTokenyWalletFailed && !wallet?.walletAddress && !wallet?.walletConnect)
                        dispatch(setAddWallet(true));
                      else dispatch(setBankToCrypto(true));
                    } else {
                      dispatch(setAuthenticationRequireHelper(true));
                    }
                  }}
                />
              )}
            </div>
            <div className="flex flex-row justify-center">
              {((origin === FromURL.Partner &&
                identityPartner?.mode === "TOKEN" &&
                identityPartner?.token?.address) ||
                origin === FromURL.None) && (
                <Button
                  theme={Themes.iconButton}
                  icon={<ChevronLeftIcon />}
                  size="lg"
                  className={twMerge(
                    "mb-2 h-10 w-10 rounded-full border-2 xl:mb-4",
                    (origin === FromURL.Partner ||
                      identityPartner?.trigger_type === "PARTNER" ||
                      !checkPermission(null, userProfile, companyDetails, identityPartner) ||
                      balanceKypayOnChain === "0,00" ||
                      isWalletLoading ||
                      walletExist ||
                      isTokenyWalletFailed) &&
                      "opacity-20"
                  )}
                  {...(!checkPermission(null, userProfile, companyDetails, identityPartner)
                    ? {
                        "data-tooltip": t("atoms:molecules.kyc_alert.kyc_warning")
                      }
                    : {
                        "data-tooltip": t(
                          "wallet:kypay.modal_deposit_funds.label.transfer_offchain"
                        )
                      })}
                  onKeyDown={preventEnterEvent}
                  onClick={async () => {
                    if (hasOneTOTP) {
                      if (
                        !wallet?.walletAddress &&
                        !wallet?.walletConnect &&
                        ready &&
                        authenticated
                      )
                        await logout();
                      if (
                        !wallet?.walletAddress &&
                        !isTokenyWalletFailed &&
                        !wallet?.walletConnect &&
                        actions.cryptoToBank
                      ) {
                        dispatch(setAddWallet(true));
                      } else {
                        dispatch(setCryptoToBank(true));
                      }
                    } else {
                      dispatch(setAuthenticationRequireHelper(true));
                    }
                  }}
                  disabled={
                    origin === FromURL.Partner ||
                    identityPartner?.trigger_type === "PARTNER" ||
                    !checkPermission(null, userProfile, companyDetails, identityPartner) ||
                    isWalletLoading ||
                    balanceKypayOnChain === "0,00" ||
                    walletExist ||
                    isTokenyWalletFailed
                  }
                />
              )}
            </div>
          </div>
        </div>
        {origin === FromURL.None ? (
          <div
            className={twMerge(
              "flex w-full flex-col p-4 lg:w-1/4 lg:p-0",
              origin !== FromURL.None ? "lg:flex-row lg:justify-between" : "lg:justify-start"
            )}
          >
            <FetchingWalletComponent />
            <div className="flex flex-row justify-between px-2 lg:justify-start">
              <Base className="font-bold text-lg">KYP: </Base>
              <div className="flex flex-row">
                <Base className="ml-2 font-bold text-lg text-primary lg:ml-6 lg:text-2xl">
                  {balanceKypayOnChain}{" "}
                </Base>
                <Button
                  theme={Themes.iconButton}
                  icon={<ArrowPathIcon />}
                  data-tooltip={t("wallet:kypay.balance_card.refresh_status")}
                  size="md"
                  className={twMerge("ml-2 hidden text-primary md:block dark:text-primary")}
                  loading={isLoadingBalanceKypayOnChain}
                  onClick={async () => {
                    await refetchAccount();
                  }}
                />
              </div>
            </div>

            <div className="flex flex-row justify-center justify-between lg:justify-start">
              <Button
                size="md"
                theme={Themes.white}
                disabled={true}
                onKeyDown={preventEnterEvent}
                onClick={async () => {
                  if (hasOneTOTP) {
                    if (!wallet?.walletAddress && !wallet?.walletConnect && ready && authenticated)
                      await logout();
                    if (
                      !wallet?.walletAddress &&
                      !isTokenyWalletFailed &&
                      !wallet?.walletConnect &&
                      actions.cryptoToBank
                    )
                      dispatch(setAddWallet(true));
                    else dispatch(setCryptoToBank(true));
                  } else {
                    dispatch(setAuthenticationRequireHelper(true));
                  }
                }}
                className={"mt-2 mt-5 w-max"}
              >
                {t("wallet:kypay.button.transfer")}
              </Button>
              {!wallet && !isAutoFetchingWallet && (
                <Button
                  size="md"
                  theme={Themes.white}
                  disabled={isTokenyWalletFailed || !!wallet}
                  onClick={async () => {
                    dispatch(setAddWallet(true));
                  }}
                  className="mt-4 ml-2 flex w-26 flex-row justify-start bg-white bg-white text-black text-primary text-sm dark:bg-white dark:text-black dark:text-white hover:dark:bg-slate-200"
                >
                  {t("wallet:kypay.modal_add_wallet.label.warning_action_required_button")}
                </Button>
              )}
              <div className="flex flex-col justify-center">
                <Button
                  theme={Themes.iconButton}
                  icon={<ChevronUpIcon />}
                  size="lg"
                  className={twMerge(
                    "h-10 w-10 rounded-full border-2 md:hidden xl:mb-4",
                    (!checkPermission(null, userProfile, companyDetails, identityPartner) ||
                      isWalletLoading ||
                      balanceKypayOnChain === "0,00" ||
                      walletExist ||
                      isTokenyWalletFailed) &&
                      "opacity-20"
                  )}
                  onKeyDown={preventEnterEvent}
                  onClick={() => {
                    if (hasOneTOTP) {
                      if (
                        !wallet?.walletAddress &&
                        !isTokenyWalletFailed &&
                        !wallet?.walletConnect &&
                        actions.cryptoToBank
                      )
                        dispatch(setAddWallet(true));
                      else dispatch(setCryptoToBank(true));
                    } else {
                      dispatch(setAuthenticationRequireHelper(true));
                    }
                  }}
                  {...(!checkPermission(null, userProfile, companyDetails, identityPartner)
                    ? {
                        "data-tooltip": t("atoms:molecules.kyc_alert.kyc_warning")
                      }
                    : {
                        "data-tooltip": t(
                          "wallet:kypay.modal_deposit_funds.label.transfer_offchain"
                        )
                      })}
                  disabled={
                    !checkPermission(null, userProfile, companyDetails, identityPartner) ||
                    balanceKypayOnChain === "0,00" ||
                    isWalletLoading ||
                    walletExist ||
                    isTokenyWalletFailed
                  }
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col p-0 p-4 lg:w-1/3 lg:py-4">
            <FetchingWalletComponent />
            {origin === FromURL.Partner &&
              identityPartner?.mode === "TOKEN" &&
              identityPartner?.token?.address && (
                <div className="flex flex-row justify-between lg:justify-start">
                  <Base className="font-bold lg:text-lg">{identityPartner?.token.symbol} : </Base>
                  <div className="flex flex-row">
                    <Base className="ml-2 font-bold text-lg text-primary lg:ml-6 lg:text-2xl">
                      {balanceKypayOnChain}{" "}
                    </Base>
                    <Button
                      theme={Themes.iconButton}
                      icon={<ArrowPathIcon />}
                      data-tooltip={t("wallet:kypay.balance_card.refresh_status")}
                      size="md"
                      className={twMerge("ml-2 hidden text-primary md:block dark:text-primary")}
                      loading={isLoadingBalanceKypayOnChain}
                      onClick={async () => {
                        await refetchAccount();
                      }}
                    />
                  </div>
                </div>
              )}
            {origin === FromURL.Partner &&
              identityPartner?.mode === "TOKEN" &&
              identityPartner?.token?.address && (
                <div className="flex flex-col justify-between lg:justify-start xl:flex-row">
                  <Button
                    size="md"
                    theme={Themes.white}
                    disabled={true}
                    onClick={() => {}}
                    className={"mt-5 w-max"}
                  >
                    {t("wallet:kypay.button.transfer")}
                  </Button>
                  {!wallet && !isAutoFetchingWallet && identityPartner?.token && (
                    <Button
                      size="md"
                      theme={Themes.white}
                      disabled={isTokenyWalletFailed || !!wallet}
                      onClick={async () => {
                        dispatch(setAddWallet(true));
                      }}
                      className="mt-2 mb-2 flex w-max flex-row justify-start bg-white bg-white text-black text-primary text-sm sm:mb-0 xl:mt-5 xl:ml-2 dark:bg-white dark:text-black dark:text-white hover:dark:bg-slate-200"
                    >
                      {t("wallet:kypay.modal_add_wallet.label.warning_action_required_button")}
                    </Button>
                  )}
                  <div className="flex flex-col justify-center">
                    {origin === FromURL.Partner &&
                      identityPartner?.mode === "TOKEN" &&
                      identityPartner?.token?.address && (
                        <Button
                          theme={Themes.iconButton}
                          icon={<ChevronUpIcon />}
                          size="lg"
                          className={twMerge(
                            "h-10 w-10 rounded-full border-2 md:hidden xl:mb-4",
                            (origin === FromURL.Partner ||
                              identityPartner?.trigger_type === "PARTNER" ||
                              !checkPermission(
                                null,
                                userProfile,
                                companyDetails,
                                identityPartner
                              ) ||
                              balanceKypayOnChain === "0,00" ||
                              isWalletLoading ||
                              isTokenyWalletFailed) &&
                              "opacity-20"
                          )}
                          onKeyDown={preventEnterEvent}
                          onClick={() => {
                            if (hasOneTOTP) {
                              if (
                                !wallet?.walletAddress &&
                                !isTokenyWalletFailed &&
                                !wallet?.walletConnect &&
                                actions.cryptoToBank
                              )
                                dispatch(setAddWallet(true));
                              else dispatch(setCryptoToBank(true));
                            } else {
                              dispatch(setAuthenticationRequireHelper(true));
                            }
                          }}
                          disabled={
                            origin === FromURL.Partner ||
                            identityPartner?.trigger_type === "PARTNER" ||
                            !checkPermission(null, userProfile, companyDetails, identityPartner) ||
                            balanceKypayOnChain === "0,00" ||
                            isWalletLoading ||
                            walletExist ||
                            isTokenyWalletFailed
                          }
                        />
                      )}
                  </div>
                </div>
              )}
          </div>
        )}
      </div>
    </>
  );
};
